import { useState } from "react";
import Button from "ui/buttons/Button";
import { Tooltip } from "./Tooltip";

export function Wrapper({ children }) {
  return <Tooltip.Provider>{children}</Tooltip.Provider>;
}

<Wrapper>

## Basic Tooltip

<Tooltip.Root>
  <Tooltip.Trigger>
    <Button btnStyle="secondary">Hover over me</Button>
  </Tooltip.Trigger>
  <Tooltip.Content>This is a basic tooltip</Tooltip.Content>
</Tooltip.Root>

---

## Positions

<div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", maxWidth: "300px" }}>
  <Tooltip.Root>
    <Tooltip.Trigger>
      <Button btnStyle="secondary" style={{ width: "100%" }}>
        Top
      </Button>
    </Tooltip.Trigger>
    <Tooltip.Content side="top">Top tooltip</Tooltip.Content>
  </Tooltip.Root>
  <Tooltip.Root>
    <Tooltip.Trigger>
      <Button btnStyle="secondary" style={{ width: "100%" }}>
        Right
      </Button>
    </Tooltip.Trigger>
    <Tooltip.Content side="right">Right tooltip</Tooltip.Content>
  </Tooltip.Root>
  <Tooltip.Root>
    <Tooltip.Trigger>
      <Button btnStyle="secondary" style={{ width: "100%" }}>
        Bottom
      </Button>
    </Tooltip.Trigger>
    <Tooltip.Content side="bottom">Bottom tooltip</Tooltip.Content>
  </Tooltip.Root>
  <Tooltip.Root>
    <Tooltip.Trigger>
      <Button btnStyle="secondary" style={{ width: "100%" }}>
        Left
      </Button>
    </Tooltip.Trigger>
    <Tooltip.Content side="left">Left tooltip</Tooltip.Content>
  </Tooltip.Root>
</div>

---

## Rich Content

<Tooltip.Root>
  <Tooltip.Trigger>
    <Button btnStyle="secondary">Rich tooltip</Button>
  </Tooltip.Trigger>
  <Tooltip.Content style={{ maxWidth: "250px" }}>
    <div>
      <strong>Important Information</strong>
      <p style={{ margin: "8px 0 0", fontSize: "12px" }}>Tooltip with formatted content.</p>
    </div>
  </Tooltip.Content>
</Tooltip.Root>

---

## Controlled

export function ControlledTooltip() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
      <Tooltip.Root open={open} onOpenChange={setOpen}>
        <Tooltip.Trigger>
          <Button btnStyle="secondary">Controlled trigger</Button>
        </Tooltip.Trigger>
        <Tooltip.Content>Controlled tooltip</Tooltip.Content>
      </Tooltip.Root>
      <Button btnStyle="tertiary" onClick={() => setOpen(!open)}>
        Toggle: {open ? "Hide" : "Show"}
      </Button>
    </div>
  );
}

<ControlledTooltip />

---

## Delay Options

<div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
  <Tooltip.Root delayDuration={0}>
    <Tooltip.Trigger>
      <Button btnStyle="secondary">No delay</Button>
    </Tooltip.Trigger>
    <Tooltip.Content>Instant (0ms)</Tooltip.Content>
  </Tooltip.Root>
  <Tooltip.Root delayDuration={500}>
    <Tooltip.Trigger>
      <Button btnStyle="secondary">500ms</Button>
    </Tooltip.Trigger>
    <Tooltip.Content>Half second</Tooltip.Content>
  </Tooltip.Root>
  <Tooltip.Root delayDuration={1000}>
    <Tooltip.Trigger>
      <Button btnStyle="secondary">1s</Button>
    </Tooltip.Trigger>
    <Tooltip.Content>One second</Tooltip.Content>
  </Tooltip.Root>
</div>

---

## On Disabled Button

Wrap in a span to show tooltip on disabled elements:

<Tooltip.Root>
  <Tooltip.Trigger>
    <span style={{ display: "inline-block" }}>
      <Button btnStyle="primary" disabled style={{ pointerEvents: "none" }}>
        Disabled button
      </Button>
    </span>
  </Tooltip.Trigger>
  <Tooltip.Content>This action is unavailable</Tooltip.Content>
</Tooltip.Root>

---

## Inline Text

Hover over the <Tooltip.Root><Tooltip.Trigger><span style={{ textDecoration: "underline dotted", cursor: "help" }}>highlighted text</span></Tooltip.Trigger><Tooltip.Content>Inline help</Tooltip.Content></Tooltip.Root> to see the tooltip. Also works with <Tooltip.Root><Tooltip.Trigger><abbr style={{ cursor: "help" }}>API</abbr></Tooltip.Trigger><Tooltip.Content>Application Programming Interface</Tooltip.Content></Tooltip.Root> abbreviations.

---

## Click-to-open

A click-mode tooltip is a controlled `Tooltip.Root` whose trigger toggles `open`, with hover opens
ignored. Procure's `WarningTooltip` is built this way. The pattern only holds if a consumer can tell
apart a close the user asked for (click, Escape, outside press) from one the pointer caused by
leaving the trigger, because only the first kind should dismiss a click-opened tooltip.

The panel below logs every event the wrapper surfaces, so the close path is visible.

**How to read it**

1. Hover **A** and move away without clicking. The log should show `pointerenter` then
   `pointerleave` and nothing else. An `onOpenChange(true)` here means hover opens are leaking past
   `disableHoverableContent`.
2. Click **A**. It opens, and the log shows `click on trigger`.
3. With it open, move the pointer off the trigger and hold still. Watch whether the tooltip stays
   up. An `onOpenChange(false)` with no click before it is a pointer-driven close, which this
   pattern should not do.
4. Click **A** again to dismiss it. Then reopen and press `Escape`; reopen once more and click
   elsewhere on the page. All three should close it and log `onOpenChange(false)`.
5. Hover **B** for contrast. It's an ordinary hover tooltip, so leaving the trigger *should* close it.

`onOpenChange` receives only the boolean, so the log can't name the reason for a close. Reading
`eventDetails.reason`, the way `Popover.Root` already forwards it, is what would let step 3 be
filtered rather than merely observed.

export function ClickToOpenHarness() {
  const [log, setLog] = useState([]);
  const [clickOpen, setClickOpen] = useState(false);
  const record = (entry) => setLog((prev) => [entry, ...prev].slice(0, 14));
  const triggerStyle = {
    display: "inline-block",
    padding: "6px 12px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    cursor: "pointer",
    userSelect: "none",
  };
  return (
    <div style={{ display: "flex", gap: "32px", alignItems: "flex-start", flexWrap: "wrap" }}>
      <div style={{ display: "flex", gap: "24px" }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: "8px" }}>A · click mode</div>
          <Tooltip.Root
            open={clickOpen}
            disableHoverableContent
            onOpenChange={(open) => {
              record("onOpenChange(" + open + ")");
              if (!open) {
                setClickOpen(false);
              }
            }}
          >
            <Tooltip.Trigger>
              <span
                role="button"
                tabIndex={0}
                style={triggerStyle}
                onClick={() => {
                  record("click on trigger");
                  setClickOpen((open) => !open);
                }}
                onPointerEnter={() => record("pointerenter")}
                onPointerLeave={() => record("pointerleave")}
              >
                Click me
              </span>
            </Tooltip.Trigger>
            <Tooltip.Content side="right">Dismiss with a click, Escape or an outside press</Tooltip.Content>
          </Tooltip.Root>
        </div>
        <div>
          <div style={{ fontWeight: 600, marginBottom: "8px" }}>B · hover mode</div>
          <Tooltip.Root>
            <Tooltip.Trigger>
              <span style={triggerStyle}>Hover me</span>
            </Tooltip.Trigger>
            <Tooltip.Content side="right">Closes when the pointer leaves</Tooltip.Content>
          </Tooltip.Root>
        </div>
      </div>
      <div style={{ minWidth: "280px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span style={{ fontWeight: 600 }}>Event log (newest first)</span>
          <Button btnStyle="tertiary" btnSize="small" onClick={() => setLog([])}>
            Clear
          </Button>
        </div>
        <ol
          style={{
            margin: 0,
            padding: "8px 12px",
            listStyle: "none",
            border: "1px solid #eee",
            borderRadius: "4px",
            minHeight: "180px",
            fontFamily: "monospace",
            fontSize: "12px",
            lineHeight: 1.8,
          }}
        >
          {log.length === 0 && <li style={{ opacity: 0.5 }}>Interact with A or B…</li>}
          {log.map((entry, index) => (
            <li key={entry + index}>{entry}</li>
          ))}
        </ol>
      </div>
    </div>
  );
}

<ClickToOpenHarness />

</Wrapper>
